<template>
  <v-container fluid>
    <v-btn icon @click="$router.back()">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>
    <span class="display-1 pl-3" id="title">納品明細書・報告書管理</span>

    <v-row align="center" justify="center" dense>
      <v-col cols="12" xl="12" dense>
        <v-card v-if= "!isSystemAdmin" flat outlined class="ma-5 pa-3">
          <div class="subtitle-2 mb-2">
            <v-icon class="mr-1" color="primary" small>mdi-information-outline</v-icon>
            <span class="primary--text">メンテナンス機能について</span>
          </div>
          <p class="caption">
              この機能はシステム管理者のみ利用できる機能です。<br>
          </p>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if= "isSystemAdmin" >
      <v-col cols="3">
        <div class="px-3">
          <v-text-field
            v-model="companySearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="会社名で検索"
          ></v-text-field>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="px-3">
          <v-text-field
            v-model="orderNumberSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="受注番号で検索"
          ></v-text-field>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="px-3">
          <v-text-field
            v-model="sampleNumberSearch"
            prepend-inner-icon="mdi-magnify"
            placeholder="依頼番号で検索"
          ></v-text-field>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="px-3">
        <v-text-field
          v-model="searchText"
          prepend-inner-icon="mdi-magnify"
          placeholder="ファイル名で検索"
        ></v-text-field>
        </div>
      </v-col>
    </v-row>

    <v-row v-if= "isSystemAdmin" >
      <v-col cols="12">
        総件数：{{filteredItems.length}}件
      </v-col>

      <v-col cols="12" xl="12" dense>
        <v-data-table
          ref="dataTable"
          :headers="headers"
          :items="filteredItems"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          :sort-by="['createdAt']"
          :sort-desc="[true]"
          :loading="loading"
          class="elevation-1"
          loading-text="読込中"
          no-data-text="データがありません。"          
          no-results-text="検索条件に該当するデータがありません。"                  
          hide-default-footer
          @page-count="pageCount = $event"
          @click:row="selectDeliverable"
          id="row-pointer"
        >
          <template v-slot:[`item.Type`]="{ item }">
            <v-chip small :color="getTypeColor(item.Type)" class="white--text type-chip">
              {{ getTypeDisplayName(item.Type) }}
            </v-chip>
          </template>
          <template v-slot:[`item.Status`]="{ item }">
            <v-chip small :color="getStatusColor(item.Status)" class="white--text">
              {{ item.Status }}
            </v-chip>
          </template>
          <template v-slot:[`item.CompanyOffice`]="{ item }">
            {{ getCompanyOffice(item) }}
          </template>
          <template v-slot:[`item.FileName`]="{ item }">
            {{ getFileNameOnly(item.FileName) }}
          </template>
          <template v-slot:[`item.DownloadDeadline`]="{ item }">
            {{ item.DownloadDeadline | moment('YYYY[年]MM[月]DD[日]') }}
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
            {{ item.createdAt | moment('YYYY[年]MM[月]DD[日] HH:mm:ss') }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row v-if= "isSystemAdmin" justify="center" dense>
      <v-col cols="10" lg="10" md='10' sm='10' xs="10">
        <v-pagination
         v-model="page"
         :total-visible="totalVisible"
         :length="pageCount"
         color="amber darken-1 black--text"
        light 
        circle>
        </v-pagination>
      </v-col>
      <v-col cols="4" lg="2"  md='4' sm='4' xs="6">
        <v-select
          :value="itemsPerPage"
          :items = "itemsPerPageList"
          label="1ページの行数"
          @input="itemsPerPage = $event"
        ></v-select>
      </v-col>
    </v-row>

    <v-dialog v-model="viewMessage" max-width="70%">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          <span class="ml-2">納品明細書・報告書詳細<span v-if="deliverable" class="body-2">（{{ deliverable.id }}）</span></span>
        </v-card-title>
        <v-card-text v-if="deliverable" class="mt-4">
          <v-row dense v-if="deliverable.Type === '納品書' || deliverable.Type === '納品書その他'">
            <v-col cols="4">
              <span class="caption">受注番号</span><br>
              <span class="body-1">{{ deliverable.OrderNumber }}</span>
            </v-col>
            <v-col cols="4">
              <span class="caption">タイプ</span><br>
              <v-chip small :color="getTypeColor(deliverable.Type)" class="white--text">
                {{ getTypeDisplayName(deliverable.Type) }}
              </v-chip>
            </v-col>
            <v-col cols="4">
              <span class="caption">ステータス</span><br>
              <v-chip small :color="getStatusColor(deliverable.Status)" class="white--text" @click="isSuperUser ? openStatusUpdateDialog() : null" :style="isSuperUser ? 'cursor: pointer;' : 'cursor: default;'">
                {{ deliverable.Status }}
              </v-chip>
            </v-col>
          </v-row>
          <v-row dense v-else>
            <v-col cols="2">
              <span class="caption">受注番号</span><br>
              <span class="body-1">{{ deliverable.OrderNumber }}</span>
            </v-col>
            <v-col cols="2">
              <span class="caption">依頼番号</span><br>
              <span class="body-1">{{ deliverable.SampleNumber || '-' }}</span>
            </v-col>
            <v-col cols="2">
              <span class="caption">サンプルID</span><br>
              <span class="body-1">{{ deliverable.SampleID || '-' }}</span>
            </v-col>
            <v-col cols="3">
              <span class="caption">タイプ</span><br>
              <v-chip small :color="getTypeColor(deliverable.Type)" class="white--text">
                {{ getTypeDisplayName(deliverable.Type) }}
              </v-chip>
            </v-col>
            <v-col cols="3">
              <span class="caption">ステータス</span><br>
              <v-chip small :color="getStatusColor(deliverable.Status)" class="white--text" @click="isSuperUser ? openStatusUpdateDialog() : null" :style="isSuperUser ? 'cursor: pointer;' : 'cursor: default;'">
                {{ deliverable.Status }}
              </v-chip>
            </v-col>
          </v-row>
          <v-divider class="my-3"></v-divider>
          <v-row dense>
            <v-col cols="6">
              <span class="caption">登録日時</span><br>
              <span class="body-1">{{ formatDateToJST(deliverable.createdAt) }}</span>
            </v-col>
            <v-col cols="6">
              <span class="caption">ダウンロード期限</span><br>
              <span 
                class="body-1" 
                @click="isSuperUser ? openDeadlineUpdateDialog(deliverable) : null" 
                :style="isSuperUser ? 'cursor: pointer; text-decoration: underline; color: blue;' : 'cursor: default;'"
              >
                {{ formatDateOnly(deliverable.DownloadDeadline) }}
              </span>
            </v-col>
          </v-row>
          <v-divider class="my-3"></v-divider>
          <v-row dense>
            <v-col cols="6">
              <span class="caption">会社名</span><br>
              <span class="body-1">{{ deliverable.Company || '-' }}</span>
            </v-col>
            <v-col cols="6">
              <span class="caption">事業所名</span><br>
              <span class="body-1">{{ deliverable.Office || '-' }}</span>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <span class="caption">サンプル名</span><br>
              <span class="body-1">{{ deliverable.SampleName || '-' }}</span>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <span class="caption">ファイル名</span><br>
              <span class="body-1">{{ getFileNameOnly(deliverable.FileName) }}</span>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="4">
              <span class="caption">ダウンロード回数</span><br>
              <span class="body-1">{{ deliverable.DownloadCount || 0 }}回</span>
            </v-col>
            <v-col cols="4">
              <span class="caption">ダウンロード日</span><br>
              <span class="body-1">{{ deliverable.DownloadAt ? formatDateToJST(deliverable.DownloadAt) : '-' }}</span>
            </v-col>
            <v-col cols="4">
              <span class="caption">ダウンロード担当者</span><br>
              <span class="body-1">{{ deliverable.DownloadUser || '-' }}</span>
            </v-col>
          </v-row>
          <v-divider class="my-3"></v-divider>
          <v-row dense>
            <v-col cols="12">
              <span class="caption">Memo</span><br>
              <span class="body-1">{{ deliverable.Memo || '-' }}</span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn color="orange" rounded @click="downloadFile" class="ma-4">
            <v-icon class="mr-2">mdi-download</v-icon>
            ダウンロード
          </v-btn>
          <v-btn color="orange" rounded @click="showDownloadHistory" class="ma-4">
            <v-icon class="mr-2">mdi-history</v-icon>
            ダウンロード履歴
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="orange" rounded @click="viewMessage=false" class="ma-4">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ダウンロード履歴ダイアログ -->
    <v-dialog v-model="isDownloadHistoryDialog" max-width="70%">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          <span class="ml-2">ダウンロード履歴</span>
        </v-card-title>
        <v-card-text class="mt-4">
          <v-data-table
            :headers="downloadHistoryHeaders"
            :items="downloadHistory"
            :loading="downloadHistoryLoading"
            loading-text="読込中"
            no-data-text="ダウンロード履歴がありません。"
            class="elevation-1"
            :sort-by="['DownloadAt']"
            :sort-desc="[false]"
            :page.sync="historyPage"
            :items-per-page="historyItemsPerPage"
            hide-default-footer
            @page-count="historyPageCount = $event"
          >
            <template v-slot:[`item.DownloadAt`]="{ item }">
              {{ formatDateToJST(item.DownloadAt) }}
            </template>
          </v-data-table>
          
          <v-row justify="center" dense class="mt-3">
            <v-col cols="10">
              <v-pagination
                v-model="historyPage"
                :total-visible="totalVisible"
                :length="historyPageCount"
                color="amber darken-1 black--text"
                light
                circle
              ></v-pagination>
            </v-col>
            <v-col cols="2">
              <v-select
                :value="historyItemsPerPage"
                :items="itemsPerPageList"
                label="1ページの行数"
                @input="historyItemsPerPage = $event"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="orange" rounded @click="isDownloadHistoryDialog=false" class="ma-4">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ステータス更新ダイアログ -->
    <v-dialog v-model="isStatusUpdateDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          <span class="ml-2">ステータス更新</span>
        </v-card-title>
        <v-card-text class="mt-4">
          <v-select
            v-model="selectedStatus"
            :items="statusUpdateOptions"
            item-text="text"
            item-value="value"
            label="ステータス"
            outlined
          ></v-select>
          <v-textarea
            v-model="selectedMemo"
            label="Memo"
            outlined
            rows="3"
            class="mt-3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="isStatusUpdateDialog=false">キャンセル</v-btn>
          <v-btn color="orange" @click="updateStatus" :loading="statusUpdateLoading">更新</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ダウンロード期限変更ダイアログ -->
    <v-dialog v-model="isDeadlineUpdateDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline grey lighten-2">
          <span class="ml-2">ダウンロード期限変更</span>
        </v-card-title>
        <v-card-text class="mt-4">
          <v-date-picker
            v-model="selectedDeadline"
            full-width
            locale="ja"
            :min="new Date().toISOString().substr(0, 10)"
          ></v-date-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="isDeadlineUpdateDialog=false">キャンセル</v-btn>
          <v-btn color="orange" @click="updateDeadline" :loading="deadlineUpdateLoading">更新</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    </v-container>
</template>

<script>
import { fetchAllDeliverabless } from '../utils/utils'
import { API, graphqlOperation } from "aws-amplify"
export default {

  name: "MainteDeliverables",
  mixins:[],
  head: {
    title: { inner: '納品明細書・報告書管理'},
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      itemsPerPageList: [ 5,10,30 ],
      totalVisible: 7,
      loading: false,
      companySearch: '',
      orderNumberSearch: '',
      sampleNumberSearch: '',
      searchText: '', 

      items: [],
      headers: [
        {
          text: '種別',
          align: 'start',
          value: 'Type',
          class: "grey darken-2 white--text",
          width: "11%",
          sortable: false 
        },
        {
          text: '事業所名',
          align: 'start',
          value: 'CompanyOffice',
          class: "grey darken-2 white--text",
          width: "12%",
          sortable: false 
        },
        {
          text: '受注番号',
          align: 'start',
          value: 'OrderNumber',
          class: "grey darken-2 white--text",
          width: "10%",
          sortable: false 
        },
        {
          text: '依頼番号',
          align: 'start',
          value: 'SampleNumber',
          class: "grey darken-2 white--text",
          width: "8%",
          sortable: false 
        },
        {
          text: 'サンプル名',
          align: 'start',
          value: 'SampleName',
          class: "grey darken-2 white--text",
          width: "12%",
          sortable: false 
        },
        {
          text: 'ファイル名',
          align: 'start',
          value: 'FileName',
          class: "grey darken-2 white--text",
          width: "13%",
          sortable: false 
        },
        {
          text: 'ステータス',
          align: 'start',
          value: 'Status',
          class: "grey darken-2 white--text",
          width: "8%",
          sortable: false 
        },
        {
          text: 'CNT',
          align: 'start',
          value: 'DownloadCount',
          class: "grey darken-2 white--text",
          width: "4%",
          sortable: false 
        },
        {
          text: 'ダウンロード期限',
          align: 'start',
          value: 'DownloadDeadline',
          class: "grey darken-2 white--text",
          width: "12%",
          sortable: true 
        },
        {
          text: '登録日時',
          align: 'start',
          value: 'createdAt',
          class: "grey darken-2 white--text",
          width: "13%",
          sortable: true 
        },
      ],
      viewMessage: false,
      deliverable: {},
      isDownloadHistoryDialog: false,
      downloadHistory: [],
      downloadHistoryLoading: false,
      downloadHistoryHeaders: [
        {
          text: 'ダウンロード日時',
          value: 'DownloadAt',
          class: "grey darken-2 white--text",
          width: '40%'
        },
        {
          text: 'ユーザー',
          value: 'DownloadUser',
          class: "grey darken-2 white--text",
          width: '30%'
        },
        {
          text: 'タイプ',
          value: 'Type',
          class: "grey darken-2 white--text",
          width: '30%'
        }
      ],
      isStatusUpdateDialog: false,
      selectedStatus: '',
      selectedMemo: '',
      statusUpdateLoading: false,
      historyPage: 1,
      historyPageCount: 0,
      historyItemsPerPage: 10,
      statusUpdateOptions: [
        { value: 'STOP', text: 'STOP（ダウンロード不可）' },
        { value: 'READY', text: 'READY（ダウンロード可）' }
      ],
      isDeadlineUpdateDialog: false,
      selectedDeadline: '',
      deadlineUpdateLoading: false,
      selectedDeliverableForDeadline: null,
    }
  },
  filters: {
  },
  computed: {
    isAdmin () {
      return this.$store.state.isAdmin
    },
    isSystemAdmin : function() {
      return this.$store.state.isSystemAdmin
    },
    isUserUser : function() {
      return this.$store.state.isUserUser
    },
    isSuperUser : function() {
      return this.$store.state.isSuperUser
    },
    filteredItems() {
      let filtered = this.items
      
      // 会社名検索
      if (this.companySearch) {
        filtered = filtered.filter(item => 
          item.Company && item.Company.toLowerCase().includes(this.companySearch.toLowerCase())
        )
      }
      
      // 受注番号検索
      if (this.orderNumberSearch) {
        filtered = filtered.filter(item => 
          item.OrderNumber && item.OrderNumber.toLowerCase().includes(this.orderNumberSearch.toLowerCase())
        )
      }
      
      // 依頼番号検索
      if (this.sampleNumberSearch) {
        filtered = filtered.filter(item => 
          item.SampleNumber && item.SampleNumber.toLowerCase().includes(this.sampleNumberSearch.toLowerCase())
        )
      }
      
      // ファイル名検索
      if (this.searchText) {
        filtered = filtered.filter(item => 
          this.getFileNameOnly(item.FileName).toLowerCase().includes(this.searchText.toLowerCase())
        )
      }
      
      return filtered
    }
  },
  mounted: async function () {
    this.initialize()
  },
  watch: {
    page() {
      this.$nextTick(() => {
        window.scrollTo(0, 0)
      })
    }
  },
  methods: {
    initialize: async function() {
      this.loading = true
      try {
        this.items = await fetchAllDeliverabless()
      } catch (error) {
        console.error('Error loading deliverables items:', error)
      } finally {
        this.loading = false
      }
    },
    selectDeliverable: function(data) {
      this.deliverable = { ...data }
      this.viewMessage = true
    },
    getFileNameOnly: function(filePath) {
      if (!filePath) return ''
      return filePath.split('/').pop()
    },
    getTypeDisplayName(type) {
      switch(type) {
        case '納品書': return '納品明細書'
        case '納品書その他': return '納品明細書その他'
        default: return type
      }
    },
    getTypeColor(type) {
      switch(type) {
        case '納品書':
        case '納品書その他': return 'blue'
        case '報告書':
        case '報告書その他': return 'green'
        case '速報報告書': return 'orange'
        default: return 'grey'
      }
    },
    getStatusColor(status) {
      switch(status) {
        case 'READY': return 'green'
        case 'PENDING': return 'orange'
        case 'STOP': return 'red'
        default: return 'grey'
      }
    },
    cancel: function() {
      this.viewMessage = false
    },
    downloadFile: async function() {
      if (!this.deliverable || !this.deliverable.FileName) {
        console.error('No file selected for download')
        return
      }

      try {
        const { Storage } = await import('aws-amplify')
        const filePath = 'deliverable' + this.deliverable.FileName
        
        const signedURL = await Storage.get(filePath, {
          level: 'public',
          expires: 60,
          contentType: 'application/octet-stream'
        })
        
        const link = document.createElement('a')
        link.href = signedURL
        link.download = this.getFileNameOnly(this.deliverable.FileName)
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        console.log('File download initiated successfully')
      } catch (err) {
        console.error('Error downloading file:', err)
      }
    },
    showDownloadHistory: async function() {
      if (!this.deliverable) {
        console.error('No deliverable selected')
        return
      }
      
      this.isDownloadHistoryDialog = true
      this.downloadHistoryLoading = true
      
      try {
        const { downloadHistoryByDeliverables } = await import('../graphql/queries')
        const result = await API.graphql(graphqlOperation(downloadHistoryByDeliverables, {
          DeliverablesID: this.deliverable.id
        }))
        this.downloadHistory = result.data.downloadHistoryByDeliverables.items.sort((a, b) => 
          new Date(a.DownloadAt) - new Date(b.DownloadAt)
        )
        console.log('Download history loaded:', this.downloadHistory.length)
      } catch (err) {
        console.error('Error fetching download history:', err)
      }
      
      this.downloadHistoryLoading = false
    },
    formatDateToJST: function(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    formatStatus: function(status) {
      const statusMap = {
        'READY': '準備完了',
        'PENDING': '処理中',
        'STOP': '停止'
      }
      return statusMap[status] || status
    },
    formatType: function(type) {
      const typeMap = {
        '納品書': '納品明細書',
        '報告書': '報告書',
        '速報報告書': '速報報告書'
      }
      return typeMap[type] || type
    },
    formatDateOnly: function(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '年').replace(/\//g, '月') + '日'
    },
    getCompanyOffice: function(item) {
      const company = item.Company || ''
      const office = item.Office || ''
      if (company && office) {
        return company + '　' + office
      } else if (company) {
        return company
      } else if (office) {
        return office
      } else {
        return '-'
      }
    },
    openStatusUpdateDialog: function() {
      this.selectedStatus = this.deliverable.Status
      this.selectedMemo = this.deliverable.Memo || ''
      this.isStatusUpdateDialog = true
    },
    updateStatus: async function() {
      if (!this.selectedStatus || !this.deliverable) {
        return
      }
      
      this.statusUpdateLoading = true
      
      try {
        const { updateDeliverables } = await import('../graphql/mutations')
        await API.graphql(graphqlOperation(updateDeliverables, {
          input: {
            id: this.deliverable.id,
            Status: this.selectedStatus,
            Memo: this.selectedMemo
          }
        }))
        
        // ローカルデータを更新
        this.deliverable.Status = this.selectedStatus
        this.deliverable.Memo = this.selectedMemo
        const itemIndex = this.items.findIndex(item => item.id === this.deliverable.id)
        if (itemIndex !== -1) {
          this.items[itemIndex].Status = this.selectedStatus
          this.items[itemIndex].Memo = this.selectedMemo
        }
        
        this.isStatusUpdateDialog = false
        console.log('Status updated successfully')
      } catch (err) {
        console.error('Error updating status:', err)
      }
      
      this.statusUpdateLoading = false
    },
    openDeadlineUpdateDialog: function(deliverable) {
      this.selectedDeliverableForDeadline = deliverable
      if (deliverable.DownloadDeadline) {
        // JSTで表示されている日付をローカル日付に変換
        const date = new Date(deliverable.DownloadDeadline)
        this.selectedDeadline = date.toISOString().split('T')[0]
      } else {
        this.selectedDeadline = ''
      }
      this.isDeadlineUpdateDialog = true
    },
    updateDeadline: async function() {
      if (!this.selectedDeadline || !this.selectedDeliverableForDeadline) {
        return
      }
      
      this.deadlineUpdateLoading = true
      
      try {
        const { updateDeliverables } = await import('../graphql/mutations')
        // JSTの23:59:59で設定し、UTCに変換
        const jstDate = new Date(this.selectedDeadline + 'T23:59:59+09:00')
        const newDeadline = jstDate.toISOString()
        
        await API.graphql(graphqlOperation(updateDeliverables, {
          input: {
            id: this.selectedDeliverableForDeadline.id,
            DownloadDeadline: newDeadline
          }
        }))
        
        // ローカルデータを更新
        this.selectedDeliverableForDeadline.DownloadDeadline = newDeadline
        const itemIndex = this.items.findIndex(item => item.id === this.selectedDeliverableForDeadline.id)
        if (itemIndex !== -1) {
          this.items[itemIndex].DownloadDeadline = newDeadline
        }
        
        // 詳細ダイアログが開いている場合は更新
        if (this.deliverable && this.deliverable.id === this.selectedDeliverableForDeadline.id) {
          this.deliverable.DownloadDeadline = newDeadline
        }
        
        this.isDeadlineUpdateDialog = false
        console.log('Download deadline updated successfully')
      } catch (err) {
        console.error('Error updating download deadline:', err)
      }
      
      this.deadlineUpdateLoading = false
    }
  }
};
</script>
<style lang="css" scoped>
#row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
.type-chip {
  font-size: 11px;
}
</style>